<template>
  <a-modal
    v-model:visible="isOpen"
    title="删除账号"
    @cancel="handleCancel"
    @ok="handleDelete"
    okText="删除"
    cancelText="关闭"
    width="800px"
    :maskClosable="false"
    :keyboard="true"
    :destroyOnClose="true"
    :focusTriggerAfterClose="true"
    :autofocus="true"
  >
    <div class="account-container">
      <h3>账号列表</h3>
      <div class="select-all">
        <a-checkbox
          @change="handleSelectAll"
          :checked="isAllSelected"
          :indeterminate="isIndeterminate"
        >
          全选
        </a-checkbox>
      </div>
      <a-table
        :columns="columns"
        :data-source="accounts"
        :row-selection="rowSelection"
        :pagination="{ pageSize: 10 }"
        :row-key="(record) => record.strAccount"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.strStatus)">
              {{ getStatusText(record.strStatus) }}
            </a-tag>
          </template>
        </template>
      </a-table>
    </div>
  </a-modal>
</template>

<style scoped>
.account-container {
  padding: 10px;
  outline: none;
}

.select-all {
  margin-bottom: 16px;
}

h3 {
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8e8e8;
}
</style>

<script lang="ts" setup>
import {
  ref,
  computed,
  onMounted,
  defineProps,
  defineEmits,
  watch,
  nextTick,
} from "vue";
import { message } from "ant-design-vue";
import { TelegramClient } from "@/websocket/Client";

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  (e: "update:visible", visible: boolean): void;
  (e: "success"): void;
}>();

// 使用计算属性处理 visible 状态
const isOpen = computed({
  get: () => props.visible,
  set: (value) => emit("update:visible", value),
});

const accounts = ref<any[]>([]);
const selectedRowKeys = ref<string[]>([]);

// 表格列定义
const columns = [
  {
    title: "账号",
    dataIndex: "strAccount",
    key: "account",
  },
  {
    title: "状态",
    dataIndex: "strStatus",
    key: "status",
  },
  {
    title: "创建时间",
    dataIndex: "createTime",
    key: "createTime",
  },
];

// 状态颜色映射
const getStatusColor = (status: string) => {
  const statusMap: { [key: string]: string } = {
    "1": "orange", // 等待中
    "2": "blue", // 正在登录
    "3": "green", // 在线
    "4": "red", // 离线
  };
  return statusMap[status] || "default";
};

// 状态文本映射
const getStatusText = (status: string) => {
  const statusMap: { [key: string]: string } = {
    "1": "等待中",
    "2": "正在登录",
    "3": "在线",
    "4": "离线",
  };
  return statusMap[status] || "未知状态";
};

// 表格选择配置
const rowSelection = {
  selectedRowKeys,
  onChange: (selectedKeys: string[]) => {
    selectedRowKeys.value = selectedKeys;
  },
  rowKey: (record: any) => record.strAccount,
};

// 全选相关的计算属性
const isAllSelected = computed(() => {
  return (
    accounts.value.length > 0 &&
    selectedRowKeys.value.length === accounts.value.length
  );
});

const isIndeterminate = computed(() => {
  return (
    selectedRowKeys.value.length > 0 &&
    selectedRowKeys.value.length < accounts.value.length
  );
});

// 处理全选
const handleSelectAll = (e: any) => {
  selectedRowKeys.value = e.target.checked
    ? accounts.value.map((account) => account.strAccount)
    : [];
};

// 获取账号列表
const fetchAccounts = async () => {
  try {
    const username = localStorage.getItem("username");
    if (!username) {
      throw new Error("未找到登录用户名");
    }
    const client = TelegramClient.getInstance();
    const response = await client.getAccountList(username);

    if (response && Array.isArray(response.list)) {
      accounts.value = response.list;
    }
  } catch (error) {
    console.error("获取账号列表错误:", error);
    message.error("获取账号列表失败");
  }
};

// 修改 handleCancel 函数
const handleCancel = () => {
  selectedRowKeys.value = [];
  nextTick(() => {
    emit("update:visible", false);
  });
};

// 修改 handleDelete 函数
const handleDelete = async () => {
  if (selectedRowKeys.value.length === 0) {
    message.warning("请选择要删除的账号");
    return;
  }

  try {
    const username = localStorage.getItem("username");
    if (!username) {
      throw new Error("未找到登录用户名");
    }

    const client = TelegramClient.getInstance();
    for (const account of selectedRowKeys.value) {
      const result = await client.deleteAccount(username, account);
      if (!result || result.code !== 1) {
        // 假设成功状态码为0
        throw new Error(
          `删除账号 ${account} 失败: ${result?.message || "未知错误"}`
        );
      }
    }

    message.success("删除成功");
    selectedRowKeys.value = [];
    nextTick(() => {
      emit("success");
      emit("update:visible", false);
    });
  } catch (error) {
    console.error("删除账号错误:", error);
    message.error(error instanceof Error ? error.message : "删除账号失败");
  }
};

// 监听模态框的显示状态
watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible) {
      console.log("打开了删除账号弹窗");
      fetchAccounts();
    }
  }
);

// 可以保留 onMounted，但它只会在组件第一次加载时执行一次
onMounted(() => {
  console.log("删除账号组件被挂载");
});
</script>
